import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { PRIZE_STATUS, formatConversion, formatStock } from '../../../utils/loyaltyPrizes';

const withCommas = value => Number(value || 0).toLocaleString('en-US');

const formatWhen = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD MMM YY') : '—';
};

// [field, label, extra <th>/<td> className]. `field` is what gets passed to onSort and
// matches the row keys sortPrizes()/filterPrizes() already know how to compare.
const COLUMNS = [
  { field: 'postName', label: 'Prize', className: '' },
  { field: 'won', label: 'Won', className: 'n' },
  { field: 'claimed', label: 'Claimed', className: 'n' },
  { field: 'conversion', label: 'Conversion', className: 'n' },
  { field: 'codesUsed', label: 'Stock', className: '' },
  { field: 'codesLeft', label: 'Left', className: 'n' },
  { field: 'lastClaimAt', label: 'Last claim', className: '' },
  { field: 'lastUploadedAt', label: 'Imported', className: '' },
  { field: 'status', label: 'Status', className: '' },
];

const Caret = ({ sorting, field }) => {
  if (sorting[0] !== field) return null;
  return <span className="caret">{sorting[1] === 1 ? '▲' : '▼'}</span>;
};

Caret.propTypes = {
  sorting: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  field: PropTypes.string.isRequired,
};

const LoyaltyPrizesTable = ({ items, sorting, onSort, onSelectPrize }) => {
  const handleRowKeyDown = (event, row) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectPrize(row);
    }
  };

  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th key={column.field} className={column.className}>
                <button
                  type="button"
                  className={sorting[0] === column.field ? 'sorted' : ''}
                  onClick={() => onSort(column.field)}
                >
                  {column.label}
                  <Caret sorting={sorting} field={column.field} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const statusMeta = PRIZE_STATUS[row.status] || PRIZE_STATUS.empty;
            const fillPct = row.codesTotal ? (row.codesUsed / row.codesTotal) * 100 : 0;

            return (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <tr
                key={row.slugName}
                tabIndex={0}
                onClick={() => onSelectPrize(row)}
                onKeyDown={event => handleRowKeyDown(event, row)}
              >
                <td>
                  <div className="pname">{row.postName || row.slugName}</div>
                  <div className="pscope">
                    {row.slugName}
                    {row.hasPost === false ? <span className="nopost"> · no post</span> : null}
                  </div>
                </td>
                <td className="n">{withCommas(row.won)}</td>
                <td className="n">{withCommas(row.claimed)}</td>
                <td className="n">
                  <span className="rate">{formatConversion(row)}</span>
                </td>
                <td className="stockcell">
                  <div className="figs">{formatStock(row)}</div>
                  {row.codesTotal ? (
                    <div className="stockbar">
                      <i style={{ width: `${fillPct}%` }} />
                    </div>
                  ) : null}
                </td>
                <td className="n">{row.codesTotal ? withCommas(row.codesLeft) : '—'}</td>
                <td className="when">{formatWhen(row.lastClaimAt)}</td>
                <td className="when">{formatWhen(row.lastUploadedAt)}</td>
                <td>
                  <span className={`chip chip--${statusMeta.tone}`}>{statusMeta.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

LoyaltyPrizesTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    slugName: PropTypes.string,
    postName: PropTypes.string,
    active: PropTypes.bool,
    hasPost: PropTypes.bool,
    won: PropTypes.number,
    claimed: PropTypes.number,
    conversion: PropTypes.number,
    codesTotal: PropTypes.number,
    codesUsed: PropTypes.number,
    codesLeft: PropTypes.number,
    firstUploadedAt: PropTypes.string,
    lastUploadedAt: PropTypes.string,
    firstClaimAt: PropTypes.string,
    lastClaimAt: PropTypes.string,
    status: PropTypes.string,
    onDemand: PropTypes.bool,
  })).isRequired,
  sorting: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onSort: PropTypes.func.isRequired,
  onSelectPrize: PropTypes.func.isRequired,
};

export default LoyaltyPrizesTable;
